import { AllAccessibleColumns, DropdownOption } from '../../../shared/types';
import { normalizeLabel, parseNumeric } from '../../../shared/utils';

const NEED_LOCK_RULES = {
    inspection_planning_need: {
        statusField: 'pp_erledigt',
        dependencies: ['inspection_planning_need'],
    },
    ncpneed: {
        statusField: 'ncperl',
        dependencies: ['inspection_planning_need', 'ncpneed'],
    },
    cmmneed: {
        statusField: 'cmmerl',
        dependencies: ['inspection_planning_need', 'cmmneed'],
    },
    qsfaneed: {
        statusField: 'qsfaerl',
        dependencies: ['inspection_planning_need', 'qsfaneed'],
    },
} as const;

export type NeedField = keyof typeof NEED_LOCK_RULES;
type StatusField = (typeof NEED_LOCK_RULES)[NeedField]['statusField'];

type RowData = Record<string, unknown>;

type LockRuleParams = {
    field: string;
    row: RowData;
    allAccessibleColumns: AllAccessibleColumns;
};

const LOCK_TRIGGER_LABELS = new Set(['done', 'rejected']);

export const NEED_LOCK_FIELDS = Object.keys(NEED_LOCK_RULES) as NeedField[];

const STATUS_FIELDS = Array.from(
    new Set(NEED_LOCK_FIELDS.map((needField) => NEED_LOCK_RULES[needField].statusField)),
) as StatusField[];

function isNeedField(field: string): field is NeedField {
    return NEED_LOCK_FIELDS.includes(field as NeedField);
}

// Builds a lookup of status options for each downstream status field from accessible column metadata.

function getStatusOptionsByField(
    allAccessibleColumns: AllAccessibleColumns,
): Record<StatusField, DropdownOption[]> {
    return STATUS_FIELDS.reduce(
        (acc, statusField) => {
            acc[statusField] =
                allAccessibleColumns.find((column) => column.attrInternalName === statusField)
                    ?.valueOptions ?? [];
            return acc;
        },
        {} as Record<StatusField, DropdownOption[]>,
    );
}

function getLockStatusIds(options: DropdownOption[]): Set<number> {
    return options.reduce((ids, option) => {
        if (!LOCK_TRIGGER_LABELS.has(normalizeLabel(option?.label))) {
            return ids;
        }

        const parsedValue = parseNumeric(option?.value);
        if (parsedValue != null) {
            ids.add(parsedValue);
        }

        return ids;
    }, new Set<number>());
}

// Compares row status by option value

function isLockedStatusValue(value: unknown, lockStatusIds: Set<number>): boolean {
    const parsedValue = parseNumeric(value);
    return parsedValue != null && lockStatusIds.has(parsedValue);
}

// Returns true when any status dependency for the target Need field is in a lock state (Done or Rejected).

export function isNeedFieldLocked(params: LockRuleParams): boolean {
    const { field, row, allAccessibleColumns } = params;

    if (!row || !allAccessibleColumns?.length || !isNeedField(field)) {
        return false;
    }

    const statusOptionsByField = getStatusOptionsByField(allAccessibleColumns);
    const lockStatusIdsByField = STATUS_FIELDS.reduce(
        (acc, statusField) => {
            acc[statusField] = getLockStatusIds(statusOptionsByField[statusField]);
            return acc;
        },
        {} as Record<StatusField, Set<number>>,
    );
    const rule = NEED_LOCK_RULES[field];

    return rule.dependencies.some((dependentNeedField) => {
        const statusField = NEED_LOCK_RULES[dependentNeedField].statusField;
        return isLockedStatusValue(row[statusField], lockStatusIdsByField[statusField]);
    });
}